/* Skye flavor chips.
 *
 * Flavor is product option 2. Each (size, flavor) pair is its own variant with its own
 * SKU, so picking a flavor is just picking a different variant at the same price — the
 * tier discount stays baked into the variant, which is what keeps Recharge renewals
 * correct. This file never touches price, the cart, or the checkout path.
 *
 * How it plugs in: the buy box already has one .skye-qty-card per variant. This scopes
 * those cards to the selected flavor by hiding the rest, clicks the right one (the
 * existing engine handler does the real work of setting variant + selling plan), then
 * fires `skye:flavorchange` so the host can re-render. It adds no new checkout logic.
 *
 * Not every flavor has every size — Variety has no 1-box because a box is one flavor,
 * so there is nothing to vary. Three rules make that feel deliberate rather than broken:
 *   1. Keep the current size when the new flavor has it; snap to its nearest size only
 *      when it doesn't.
 *   2. Show the snapped size and price ON the chip, before it is tapped. A bubble that
 *      silently changes the price reads as bait-and-switch.
 *   3. Remember the size the user came from, so bouncing off Variety and back doesn't
 *      silently downgrade a 4-box customer to the default.
 */
(function () {
  function money(cents) { return '$' + (Number(cents) / 100).toFixed(2); }

  function init(root) {
    if (!root || root.dataset.skyeFlavorsBound) return;
    root.dataset.skyeFlavorsBound = '1';

    var form = root.closest('form') || root.parentElement;
    if (!form) return;

    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-flavor-chip]'));
    if (!chips.length) return;

    function allCards() {
      return Array.prototype.slice.call(form.querySelectorAll('.skye-qty-card'));
    }
    function cardsFor(flavor) {
      return allCards().filter(function (c) { return c.dataset.flavor === flavor; });
    }
    function barsOf(card) { return Number(card.dataset.barsCount) || 0; }

    /* Subscription price for a card's variant, so the chip hint quotes the same number
       the hero shows. Falls back to the one-time price when no plan is attached. */
    function displayPrice(card) {
      /* Quote the price the shopper would actually pay in the mode they are in. */
      var sp = form.querySelector('[data-selling-plan-input]');
      if (sp && !sp.value) return Number(card.dataset.variantPrice) || 0;
      var sub = form.querySelector(
        '[data-purchase-type="subscription"][data-variant-id="' + card.dataset.variantId + '"]'
      );
      var subCents = sub && Number(sub.dataset.sellingPlanPrice || sub.dataset.monthlyPlanPrice);
      return subCents || Number(card.dataset.variantPrice) || 0;
    }

    function currentBars() {
      var active = form.querySelector('.skye-qty-card.is-active');
      return active ? barsOf(active) : 0;
    }

    /* The size to land on: the one they already had if this flavor has it, otherwise
       the closest available, biased upward on a tie so nobody is silently downgraded. */
    function pickCard(flavor, wantBars) {
      var list = cardsFor(flavor);
      if (!list.length) return null;
      var exact = list.filter(function (c) { return barsOf(c) === wantBars; })[0];
      if (exact) return exact;
      return list.slice().sort(function (a, b) {
        var da = Math.abs(barsOf(a) - wantBars);
        var db = Math.abs(barsOf(b) - wantBars);
        if (da !== db) return da - db;
        return barsOf(b) - barsOf(a);
      })[0];
    }

    /* Remember the last size the user chose, so returning from Variety restores it
       rather than dropping to whatever Variety snapped to. Updated synchronously from
       the click that caused it - a deferred read races the engine's own handlers and
       silently remembers a stale size. */
    var rememberedBars = currentBars();
    var suppressRemember = false;

    /* The page wears the flavour, the way the event site does: four colours on :root
       that the gallery panel, sourcing map and accents all read, plus a
       data-skye-flavor attribute on <html> that shows and hides any [data-flavor-only]
       content on the page (rules generated by the picker snippet). Everything is read
       off the chip the server rendered, so there is no colour table here to drift
       from snippets/skye-flavor-meta.liquid. */
    var tintScope = root.closest('[data-skye-flavor-scope]');
    var docEl = document.documentElement;
    var ledeEl = root.querySelector('[data-flavor-lede]');
    var eyebrowEl = root.querySelector('[data-flavor-eyebrow]');
    var currentEl = root.querySelector('[data-flavor-current]');

    function paintTheme(flavor) {
      var chip = chips.filter(function (c) { return c.dataset.flavor === flavor; })[0];
      if (!chip) return;
      var d = chip.dataset;
      /* A chip with no palette keeps the last good colours rather than flashing to
         the CSS fallbacks mid-interaction. */
      if (d.flavorC) {
        docEl.style.setProperty('--skye-c', d.flavorC);
        docEl.style.setProperty('--skye-deep', d.flavorDeep);
        docEl.style.setProperty('--skye-btn', d.flavorBtn);
        docEl.style.setProperty('--skye-tint', d.flavorTint);
      }
      if (d.flavorHandle) docEl.setAttribute('data-skye-flavor', d.flavorHandle);
      if (tintScope && d.flavorTint) tintScope.style.setProperty('--skye-flavor-tint', d.flavorTint);
      if (currentEl) currentEl.textContent = flavor;
      if (ledeEl) ledeEl.textContent = d.flavorLede || '';
      if (eyebrowEl) {
        eyebrowEl.textContent = d.flavorEyebrow || '';
        eyebrowEl.hidden = !d.flavorEyebrow;
      }
    }

    function scope(flavor) {
      allCards().forEach(function (c) {
        c.hidden = (c.dataset.flavor !== flavor);
      });
    }

    function paintChips(flavor) {
      var want = rememberedBars || currentBars();
      chips.forEach(function (chip) {
        /* A sold-out chip keeps its server-rendered "Sold out" hint and never becomes
           active - it is shown greyed so the flavor is still visible, not selectable.
           A coming-soon chip IS selectable, so it only keeps its hint. */
        if (chip.disabled) return;
        if (isComing(chip)) {
          chip.classList.toggle('is-active', chip.dataset.flavor === flavor);
          chip.setAttribute('aria-checked', chip.dataset.flavor === flavor ? 'true' : 'false');
          return;
        }

        var on = chip.dataset.flavor === flavor;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-checked', on ? 'true' : 'false');

        var hint = chip.querySelector('[data-flavor-hint]');
        if (!hint) return;

        /* Only annotate a chip when tapping it would CHANGE the size — otherwise the
           hint is noise on every chip. */
        var target = pickCard(chip.dataset.flavor, want);
        if (!on && target && barsOf(target) !== want) {
          var boxes = Math.round(barsOf(target) / 7);
          hint.textContent = boxes + (boxes === 1 ? ' box · ' : ' boxes · ') + money(displayPrice(target));
          hint.hidden = false;
        } else {
          hint.textContent = '';
          hint.hidden = true;
        }
      });
    }

    /* A coming-soon flavor has no variants, so there is nothing to select in the buy
       box. Swap the content to the teaser and put the notify form where the purchase
       card was. The other chips stay visible directly above it, so the way back to a
       buyable flavor is always one tap and never has to be discovered. */
    function isComing(chip) { return !!(chip && chip.hasAttribute('data-flavor-coming-soon')); }

    function setComingSoon(on, flavor) {
      var hero = form.querySelector('[data-skye-hero]');
      var panel = root.querySelector('[data-skye-mystery]');
      if (hero) hero.hidden = !!on;
      if (panel) panel.hidden = !on;
      /* The mobile sticky bar would still check out the last buyable flavor, which the
         page is no longer showing. Hide it while the teaser is up (skye-flavors.css). */
      document.documentElement.toggleAttribute('data-skye-coming-soon', !!on);
      if (on) {
        root.dataset.currentFlavor = flavor;
        allCards().forEach(function (c) { c.hidden = true; });
        paintChips(flavor);
        paintTheme(flavor);
        root.dispatchEvent(new CustomEvent('skye:flavorchange', {
          bubbles: true, detail: { flavor: flavor, comingSoon: true }
        }));
      }
    }

    function select(flavor, opts) {
      var chip = chips.filter(function (c) { return c.dataset.flavor === flavor; })[0];
      if (isComing(chip)) { setComingSoon(true, flavor); return; }
      setComingSoon(false);

      var want = (opts && opts.bars) || rememberedBars || currentBars();
      var target = pickCard(flavor, want);
      if (!target) return;

      scope(flavor);
      root.dataset.currentFlavor = flavor;

      /* Only remember sizes the user actually reached on a full-ladder flavor; a snap
         onto Variety's 3-box must not become the size they get sent back to. */
      if (barsOf(target) === want) rememberedBars = want;

      if (!target.classList.contains('is-active')) {
        /* This click is ours, not the user's: it must not be mistaken for the user
           choosing a size, or a snap onto Variety's 3-box would overwrite the size we
           are holding for them. */
        suppressRemember = true;
        try { target.click(); }   /* engine handler: sets variant id + selling plan */
        finally { suppressRemember = false; }
      }
      paintChips(flavor);
      paintTheme(flavor);
      root.dispatchEvent(new CustomEvent('skye:flavorchange', {
        bubbles: true,
        detail: { flavor: flavor, bars: barsOf(target) }
      }));
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        if (chip.disabled) return;
        select(chip.dataset.flavor);
      });
    });

    /* A size the user picked themselves, via the hero's size links or a qty-card.
       Read the size off the clicked element rather than off the active card, so this
       does not depend on whether the engine's handler has run yet. */
    form.addEventListener('click', function (e) {
      /* Subscribe / Buy Once changes every price, so re-quote the chip hints once the
         engine has applied the new plan. */
      if (e.target.closest('[data-hero-mode], [data-purchase-type]')) {
        setTimeout(function () { paintChips(root.dataset.currentFlavor); }, 40);
      }
      if (suppressRemember) return;
      var sizeLink = e.target.closest('[data-hero-size]');
      var card = e.target.closest('.skye-qty-card');
      var bars = sizeLink ? Number(sizeLink.dataset.heroSize)
               : card ? Number(card.dataset.barsCount)
               : 0;
      if (!bars) return;
      rememberedBars = bars;
      paintChips(root.dataset.currentFlavor);
    });

    scope(root.dataset.currentFlavor);
    paintChips(root.dataset.currentFlavor);
    paintTheme(root.dataset.currentFlavor);

    bindMystery(root);
  }

  /* Mystery flavor: a locked chip that opens an inline email capture. The buy box,
     price and current selection are deliberately untouched in every state — replacing
     the CTA with a form on a paid-traffic landing page is a conversion hole with no
     floor, and a curious tap must never cost an order. */
  function bindMystery(root) {
    var panel = root.querySelector('[data-skye-mystery]');
    if (!panel) return;

    var emailEl = panel.querySelector('[data-mystery-email]');
    var submit = panel.querySelector('[data-mystery-submit]');
    var formEl = panel.querySelector('[data-mystery-form]');
    var errEl = panel.querySelector('[data-mystery-error]');
    var okEl = panel.querySelector('[data-mystery-success]');
    var listId = panel.dataset.klaviyoList;
    var companyId = panel.dataset.klaviyoCompany;


    function fail(msg) {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.hidden = false;
    }
    if (emailEl) {
      emailEl.addEventListener('input', function () { if (errEl) errEl.hidden = true; });
    }

    if (!submit) return;
    submit.addEventListener('click', function (e) {
      e.preventDefault();
      if (submit.disabled) return;
      var email = (emailEl && emailEl.value || '').trim();
      if (!email || email.indexOf('@') < 1 || email.indexOf('.') < 0) {
        fail('Enter a valid email address.');
        return;
      }
      if (errEl) errEl.hidden = true;
      submit.disabled = true;
      submit.style.opacity = '0.6';

      /* Same direct Klaviyo call the hero drawer uses (sections/hero-banner.liquid).
         Deliberately not _learnq: the onsite embed is async third-party and this
         already works in production. */
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'PDP mystery flavor',
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      })
        .then(function (res) {
          if (res.ok || res.status === 202) {
            if (formEl) formEl.hidden = true;
            if (okEl) okEl.hidden = false;
          } else {
            submit.disabled = false;
            submit.style.opacity = '';
            fail('Something went wrong. Try again.');
          }
        })
        .catch(function () {
          submit.disabled = false;
          submit.style.opacity = '';
          fail('Something went wrong. Try again.');
        });
    });
  }

  function boot() {
    document.querySelectorAll('[data-skye-flavors]').forEach(init);
  }

  if (window.__skyeReady) { window.__skyeReady(boot); }
  else if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }

  window.SkyeFlavors = { init: init, boot: boot };
})();
